<template>
  <BaseSpinner v-if="loading" />
  <EmptyPageContent
    v-else-if="!servicePackages.length"
    imageName="service-packages"
    :text="{
      title: t('package.empty_state.title'),
      description: t('package.empty_state.description'),
      btnPrimary: t('package.create'),
    }"
    @click="router.push({ name: 'admin-service-package-new' })"
    v-test="'service-package-empty-state'"
  />
  <Flex
    v-else
    direction="column"
    :gap="0.5"
  >
    <Flex
      v-for="group in servicePackageGroups"
      :key="group.category?.id ?? 'uncategorized'"
      direction="column"
      :gap="0.5"
      v-test="'service-package-category'"
    >
      <BaseHeading
        v-if="group.category"
        size="s"
        v-test="'service-package-category-name'"
      >
        {{ group.category.name }}
      </BaseHeading>
      <Draggable
        :class="$style.packages"
        :modelValue="group.servicePackages"
        :disabled="device.touch"
        itemKey="id"
        @update:modelValue="onReorder(group, $event)"
      >
        <template #item="{ element, index }">
          <Item
            :servicePackage="element"
            :expanded="isExpanded(element.id)"
            :serviceLookup="serviceLookup"
            :index="index"
            :listLength="group.servicePackages.length"
            v-test="'service-package-list-item'"
            @toggle="toggle(element.id)"
            @updateSortOrder="onSortByClick(group, index, $event)"
          />
        </template>
      </Draggable>
    </Flex>

    <RouterLink
      :class="$style.createButton"
      :to="{ name: 'admin-service-package-new' }"
      v-test="'service-package-create-list-bottom'"
    >
      <BaseIcon
        name="plus"
        color="success"
      />
      <BaseText bold>{{ t('package.create') }}</BaseText>
    </RouterLink>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import { flash } from '@/helpers/ui';
import { sortByDrag } from '@/modules/admin/helpers';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesStore } from '@/stores/services';
import type { ServicePackage } from '@/types';
import { device } from '@/user-context';
import { GET_SERVICE_PACKAGES, SORT_SERVICE_PACKAGES } from './graphql';
import Item from './Item.vue';
import { formatServiceLabel } from './serviceLabel';

type ServicePackageGroup = {
  category: ServicePackage['category'];
  servicePackages: ServicePackage[];
};

const { t } = useI18n();
const router = useRouter();

const { onResult, loading, refetch } = useQuery(GET_SERVICE_PACKAGES);

const servicePackages = ref<ServicePackage[]>([]);

onResult(({ data }) => {
  servicePackages.value = data?.servicePackages ?? [];
});

const servicePackageGroups = computed(() => {
  const groups = new Map<number | null, ServicePackageGroup>();

  servicePackages.value.forEach((servicePackage) => {
    const categoryId = servicePackage.category?.id ?? null;
    const group = groups.get(categoryId) ?? {
      category: servicePackage.category,
      servicePackages: [],
    };
    group.servicePackages.push(servicePackage);
    groups.set(categoryId, group);
  });

  return [...groups.values()];
});

const { mutate: sortServicePackages, onDone: onSorted } = useMutation(
  SORT_SERVICE_PACKAGES,
);

const { getServicePackages } = useServicesStore();
onSorted(() => getServicePackages());

const onReorder = (
  group: ServicePackageGroup,
  newServicePackages: ServicePackage[],
) => {
  const sortableAttributes = sortByDrag(
    newServicePackages,
    group.servicePackages,
  );
  const reorderedServicePackages = newServicePackages.map(
    (servicePackage, index) => ({
      ...servicePackage,
      sortOrder: sortableAttributes[index].sortOrder,
    }),
  );
  servicePackages.value = servicePackageGroups.value.flatMap((otherGroup) =>
    otherGroup.category?.id === group.category?.id
      ? reorderedServicePackages
      : otherGroup.servicePackages,
  );

  sortServicePackages({ input: { sortableAttributes } })
    .then(() => {
      flash(t('global.flash.services_updated'));
    })
    .catch(() => refetch());
};

const onSortByClick = (
  group: ServicePackageGroup,
  index: number,
  newIndex: number,
) => {
  const newServicePackages = [...group.servicePackages];
  [newServicePackages[index], newServicePackages[newIndex]] = [
    newServicePackages[newIndex],
    newServicePackages[index],
  ];
  onReorder(group, newServicePackages);
};

const { serviceGroups } = storeToRefs(useServiceGroupsStore());

const serviceLookup = computed(() => {
  const map = new Map<number, { label: string; duration: number }>();
  serviceGroups.value.forEach((group) => {
    group.services.forEach((service) => {
      map.set(service.id, {
        label: formatServiceLabel(group, service),
        duration: service.duration,
      });
    });
  });
  return map;
});

const expandedId = ref<number | null>(null);

const isExpanded = (id: number) => expandedId.value === id;

const toggle = (id: number) => {
  expandedId.value = isExpanded(id) ? null : id;
};
</script>

<style
  lang="scss"
  module
>
.packages {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
}

.createButton {
  cursor: pointer;
  border: 1px dashed var(--color-success);
  display: flex;
  padding: $spacing;
  border-radius: $radius;
  gap: $spacing * 0.5;
}
</style>
